/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Footer accordion: collapsed on mobile, always expanded on desktop
(function () {
  const footerAccordion = document.querySelector('accordion-group.footer__accordion');
  if (!footerAccordion) return;

  const mobileQuery = window.matchMedia('(max-width: 47.9275em)');

  const getFooterDetails = () =>
    footerAccordion.querySelectorAll('.footer-nav__details');

  const syncFooterLayout = () => {
    const isMobile = mobileQuery.matches;

    footerAccordion.classList.toggle(
      'footer__accordion--desktop-expanded',
      !isMobile
    );

    getFooterDetails().forEach((details) => {
      if (isMobile) {
        details.removeAttribute('open');
        details.style.height = '';
        details.style.overflow = '';
      } else {
        details.setAttribute('open', '');
        details.style.height = '';
        details.style.overflow = '';
      }
    });
  };

  syncFooterLayout();
  mobileQuery.addEventListener('change', syncFooterLayout);
  window.addEventListener('load', syncFooterLayout);

  footerAccordion.addEventListener(
    'on:accordion:opening',
    (event) => {
      if (!mobileQuery.matches) return;

      const openingDetails = event.target;
      if (!(openingDetails instanceof HTMLDetailsElement)) return;

      footerAccordion.querySelectorAll('details[open]').forEach((details) => {
        if (details !== openingDetails && details.id) {
          footerAccordion.close(details.id);
        }
      });
    },
    true
  );
})();

// Shopify Inbox (mobile): scale, right stack, show after ~1.5 screens then keep visible
(function () {
  const CHAT_SCALE = 0.7385; // 48/65 — match back-to-top visual size
  const CHAT_ORIGIN = 'bottom right';
  const CHAT_SHOW_SCREENS = 1;
  const mobileQuery = window.matchMedia('(max-width: 61.24em)');
  const CHAT_HOST_SELECTORS = ['#ShopifyChat', 'inbox-online-store-chat'];

  let chatEverRevealed = false;
  let syncing = false;
  let alignRaf = 0;
  const observedHosts = new WeakSet();

  function getChatHosts() {
    const hosts = new Set();
    CHAT_HOST_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => hosts.add(el));
    });
    return [...hosts];
  }

  function getBackToTop() {
    return document.querySelector('.back-to-top-button');
  }

  function getBaseRightPx() {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--mobile-floating-btn-right')
      .trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 12;
  }

  function isChatOpen(el) {
    return el.getAttribute('is-open') === 'true';
  }

  function setScaled(el) {
    el.style.setProperty('scale', String(CHAT_SCALE), 'important');
    el.style.setProperty('transform-origin', CHAT_ORIGIN, 'important');
    el.style.removeProperty('translate');
    el.style.removeProperty('transform');
  }

  function clearScaled(el) {
    el.style.removeProperty('scale');
    el.style.removeProperty('transform-origin');
    el.style.removeProperty('translate');
    el.style.removeProperty('transform');
  }

  function setMobilePosition(el, rightPx) {
    el.style.setProperty('right', `${rightPx}px`, 'important');
    el.style.setProperty('left', 'auto', 'important');
  }

  function clearMobilePosition(el) {
    el.style.removeProperty('right');
    el.style.removeProperty('left');
  }

  /** Align chat + back-to-top horizontal centers using measured pixels. */
  function alignChatWithBackToTop(el) {
    const backToTop = getBackToTop();
    const baseRight = getBaseRightPx();

    if (!backToTop || !mobileQuery.matches || isChatOpen(el)) {
      if (mobileQuery.matches) setMobilePosition(el, baseRight);
      return;
    }

    setMobilePosition(el, baseRight);
    void el.offsetWidth;

    const chatRect = el.getBoundingClientRect();
    const btnRect = backToTop.getBoundingClientRect();
    if (chatRect.width < 1 || btnRect.width < 1) return;

    const chatCenter = chatRect.left + chatRect.width / 2;
    const btnCenter = btnRect.left + btnRect.width / 2;
    const delta = btnCenter - chatCenter;
    const alignedRight = Math.round((baseRight - delta) * 10) / 10;

    setMobilePosition(el, alignedRight);
  }

  function scheduleAlign(hosts) {
    cancelAnimationFrame(alignRaf);
    alignRaf = requestAnimationFrame(() => {
      hosts.forEach((el) => {
        if (!isChatOpen(el)) alignChatWithBackToTop(el);
      });
    });
  }

  function revealChatHosts(hosts) {
    hosts.forEach((el) => {
      if (!el.classList.contains('mobile-chat-revealed')) {
        el.classList.add('mobile-chat-revealed');
      }
    });
  }

  function hideChatHosts(hosts) {
    hosts.forEach((el) => {
      if (el.classList.contains('mobile-chat-revealed')) {
        el.classList.remove('mobile-chat-revealed');
      }
    });
  }

  function applyMobileChatScale() {
    const hosts = getChatHosts();
    const baseRight = getBaseRightPx();

    if (!mobileQuery.matches) {
      hosts.forEach((el) => {
        clearScaled(el);
        clearMobilePosition(el);
        if (!el.classList.contains('mobile-chat-revealed')) {
          el.classList.add('mobile-chat-revealed');
        }
      });
      return hosts;
    }

    hosts.forEach((el) => {
      setMobilePosition(el, baseRight);
      if (isChatOpen(el)) clearScaled(el);
      else setScaled(el);
    });

    return hosts;
  }

  function getVisibleScreenHeight() {
    // Use the currently visible UI height as one "screen" unit (not layout/full page height)
    const visualH = window.visualViewport && window.visualViewport.height > 0
      ? window.visualViewport.height
      : 0;
    const clientH = document.documentElement.clientHeight || 0;
    const innerH = window.innerHeight || 0;
    const candidates = [visualH, clientH, innerH].filter((h) => h > 0);
    return candidates.length ? Math.min(...candidates) : 0;
  }

  function updateMobileChatVisibility(hosts) {
    if (!mobileQuery.matches) {
      revealChatHosts(hosts);
      return;
    }

    if (chatEverRevealed) {
      revealChatHosts(hosts);
      return;
    }

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    const screenH = getVisibleScreenHeight();
    const threshold = screenH * CHAT_SHOW_SCREENS;

    if (screenH > 0 && scrollY >= threshold) {
      chatEverRevealed = true;
      revealChatHosts(hosts);
      scheduleAlign(hosts);
    } else {
      hideChatHosts(hosts);
    }
  }

  function syncMobileChat(options) {
    if (syncing) return;
    syncing = true;

    try {
      const hosts = applyMobileChatScale();
      updateMobileChatVisibility(hosts);
      observeChatHosts(hosts);

      if (options && options.align) {
        scheduleAlign(hosts);
      }
    } finally {
      syncing = false;
    }
  }

  // Only watch chat hosts for is-open — never all class changes on the document
  const hostAttrObserver = new MutationObserver(() => {
    syncMobileChat({ align: true });
  });

  function observeChatHosts(hosts) {
    hosts.forEach((el) => {
      if (observedHosts.has(el)) return;
      observedHosts.add(el);
      hostAttrObserver.observe(el, {
        attributes: true,
        attributeFilter: ['is-open'],
      });
    });
  }

  // Watch for chat widget being injected (do not observe attributes site-wide)
  const bodyChildObserver = new MutationObserver((mutations) => {
    const added = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== 1) return false;
        return (
          node.matches?.(CHAT_HOST_SELECTORS.join(',')) ||
          node.querySelector?.(CHAT_HOST_SELECTORS.join(','))
        );
      })
    );
    if (added) syncMobileChat({ align: true });
  });

  function startBodyObserver() {
    if (!document.body) return;
    bodyChildObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) {
    startBodyObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startBodyObserver, { once: true });
  }

  syncMobileChat({ align: true });

  mobileQuery.addEventListener('change', () => {
    if (!mobileQuery.matches) chatEverRevealed = false;
    syncMobileChat({ align: true });
  });

  window.addEventListener(
    'scroll',
    () => {
      if (chatEverRevealed) return;
      updateMobileChatVisibility(getChatHosts());
    },
    { passive: true }
  );

  window.addEventListener('resize', () => syncMobileChat({ align: true }));
  window.addEventListener('load', () => syncMobileChat({ align: true }));
  document.addEventListener('shopify:section:load', () => syncMobileChat({ align: true }));

  [500, 1500, 3000].forEach((delay) => {
    window.setTimeout(() => syncMobileChat({ align: true }), delay);
  });
})();
